/** @jsxImportSource @emotion/react */
import { chargeBadgeStyle, getShortChargeIdValue } from 'components/lab/billingCharge/utils'
import routes from 'components/lab/constants/routes'
import { formatCalendarDate } from 'helpers/dates'
import { useState } from 'react'
import { SubmitBillingChargesResult } from 'store/types'

type Props = {
  report: SubmitBillingChargesResult
  onClose: () => void
}

// Longer reasons (a duplicate-DOS refusal lists every clash) are cut here, at
// a word, so every row keeps one line until the biller asks for the rest.
const REASON_PREVIEW_LENGTH = 110

const SUCCESS = { accent: '#34a853', background: '#e6f4ea', text: '#1e6b34' }
const ATTENTION = { accent: '#f0a030', background: '#fff4e5', text: '#8a5300' }

const chargeHref = (patientId: number, chargeId: string): string =>
  `${routes.index}${routes.patient.root}/${patientId}${routes.billing.root}${routes.billing.charge}${routes.billing.edit}${chargeId}`

const previewOf = (reason: string): string => {
  const cut = reason.slice(0, REASON_PREVIEW_LENGTH)
  const lastSpace = cut.lastIndexOf(' ')

  return (lastSpace > REASON_PREVIEW_LENGTH / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:—-]+$/, '')
}

const Chip = ({ tone, icon, label }: { tone: typeof SUCCESS; icon: string; label: string }) => (
  <span
    css={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 600,
      background: tone.background,
      color: tone.text,
    }}>
    <i className={icon} />
    {label}
  </span>
)

const Reason = ({ reason }: { reason: string }) => {
  const [expanded, setExpanded] = useState(false)
  const preview = previewOf(reason)

  if (preview.length >= reason.length) {
    return <>{reason}</>
  }

  return (
    <>
      {expanded ? reason : `${preview}…`}{' '}
      <button
        type="button"
        className="btn btn-link p-0 align-baseline"
        css={{ fontSize: 13 }}
        onClick={() => setExpanded((open) => !open)}>
        {expanded ? 'show less' : 'show more'}
      </button>
    </>
  )
}

// What the last bulk submit did: how many charges went out, and for each one
// that could not, who it is, which charge, its appointment, why, and a link to
// open it. It stays until the biller closes it; the charges it lists stay
// selected, ready to fix and resubmit.
export const BulkSubmissionReport = ({ report, onClose }: Props) => {
  const sent = report.submitted.length
  const failed = report.failed.length

  return (
    <div
      className="mb-3"
      css={{
        background: '#fff',
        border: '1px solid #e3e6ea',
        borderLeft: `4px solid ${failed ? ATTENTION.accent : SUCCESS.accent}`,
        borderRadius: 6,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      }}>
      <div
        className="d-flex align-items-center"
        css={{ gap: 10, padding: '10px 16px', borderBottom: failed ? '1px solid #eef0f2' : 'none' }}>
        <span css={{ fontWeight: 600 }}>Submission results</span>
        <Chip tone={SUCCESS} icon="fas fa-check" label={`${sent} submitted`} />
        {failed > 0 && (
          <Chip tone={ATTENTION} icon="fas fa-exclamation-triangle" label={`${failed} need attention`} />
        )}
        {failed > 0 && (
          <span className="text-muted" css={{ fontSize: 13 }}>
            They stay selected — fix and resubmit.
          </span>
        )}
        <button
          type="button"
          aria-label="Close"
          title="Close"
          className="btn btn-sm btn-link text-muted ml-auto"
          onClick={onClose}>
          <i className="fas fa-times" />
        </button>
      </div>
      {failed > 0 && (
        // Scrolls on its own, so a hundred refusals never push the list off the page.
        <div css={{ maxHeight: 320, overflowY: 'auto' }}>
          <table
            className="table table-sm table-hover mb-0"
            css={{
              fontSize: 13,
              '& th': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: '#f8f9fa',
                color: '#6c757d',
                fontWeight: 600,
                borderTop: 'none',
                borderBottom: '1px solid #e3e6ea',
              },
              '& th, & td': { padding: '6px 16px', verticalAlign: 'top' },
              '& td': { borderTop: '1px solid #f1f3f5' },
            }}>
            <thead>
              <tr>
                <th>Patient</th>
                <th>Charge ID</th>
                <th>Appointment</th>
                <th>Reason</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {report.failed.map((failure) => (
                <tr key={failure.chargeId}>
                  <td css={{ whiteSpace: 'nowrap' }}>{failure.patientName || '-'}</td>
                  <td>
                    <span style={chargeBadgeStyle}>{getShortChargeIdValue(failure.chargeId)}</span>
                  </td>
                  <td css={{ whiteSpace: 'nowrap' }}>{formatCalendarDate(failure.appointmentDate)}</td>
                  <td css={{ width: '100%' }}>
                    <Reason reason={failure.reason} />
                  </td>
                  <td css={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {failure.patientId && (
                      <a
                        href={chargeHref(failure.patientId, failure.chargeId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open the charge in a new tab">
                        Open <i className="fas fa-external-link-alt ml-1" css={{ fontSize: 11 }} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
